import type { SlaSpec, WorkflowInstanceView } from "@ncp/api-client";
import type { CalculationMethod, CaseFigures, MethodBands } from "../../case/figures";
import type { Blackboard, Tone } from "./engine";
import type { EcFormView, EcMarketView } from "./tools";

/** One market's figures as the platform computed them, whichever endpoint carried them (the file's figures, else the form's assessment). */
export interface MarketReading {
  marketId?: string;
  productMarket: string; productMarketAr?: string | null; geographicMarket: string;
  combinedShare: number; hhiPre: number; hhiPost: number; deltaHhi: number; cr4Post: number; concentrationRatioN: number;
  band: string; assessment: string; assessmentAr?: string | null; overlap: boolean; mostAffected: boolean;
  partiesSalesInMarketAed?: number | null; methodVersion?: string;
  lines: Array<{ name: string; sharePercent: number; party: boolean; unallocated: boolean }>;
  /** The theories of harm the market's current figures support, from the platform's own rule – the ones the analysis tab lists (5.2). */
  theoriesOfHarm?: Array<{ en: string; ar: string }>;
}

/** The market the review rests on, as the platform names it. */
export interface Subject {
  market?: EcMarketView;
  reading?: MarketReading;
  /** True when the platform marked it the most affected market; false when the parties overlap nowhere (the first declared market is read). */
  mostAffected: boolean;
}

type Line = { name: string; sharePercent: number; kind?: string; isParty?: boolean; isUnallocated?: boolean };
const linesOf = (lines: Line[] | undefined) => (lines ?? []).map((l) => ({
  name: l.name, sharePercent: l.sharePercent,
  party: l.isParty ?? l.kind === "Party", unallocated: l.isUnallocated ?? l.kind === "Unallocated",
}));

/** Every market of the file with the platform's figures, in the platform's order. */
export function readingsOf(board: Blackboard): MarketReading[] {
  const figures = board.get<CaseFigures | null>("figures");
  if (figures?.markets.length) {
    return figures.markets.map(({ marketId, figures: m, theoriesOfHarm }) => ({
      marketId, productMarket: m.productMarket, productMarketAr: m.productMarketAr, geographicMarket: m.geographicMarket,
      combinedShare: m.combinedShare, hhiPre: m.hhiPre, hhiPost: m.hhiPost, deltaHhi: m.deltaHhi, cr4Post: m.cr4Post, concentrationRatioN: m.concentrationRatioN || 4,
      band: m.band, assessment: m.assessment, assessmentAr: m.assessmentAr, overlap: m.overlap, mostAffected: m.mostAffected,
      partiesSalesInMarketAed: m.partiesSalesInMarketAed, methodVersion: m.methodVersion, lines: linesOf(m.lines as Line[]), theoriesOfHarm: theoriesOfHarm ?? [],
    }));
  }
  // The file's own assessment carries the same figures and the same most-affected flag, computed by the same rule on the server.
  const form = board.get<EcFormView>("form");
  return (form?.assessment.markets ?? []).map((m) => ({
    marketId: form?.markets.find((x) => x.productMarket === m.productMarket)?.id,
    productMarket: m.productMarket, productMarketAr: m.productMarketAr, geographicMarket: m.geographicMarket,
    combinedShare: m.combinedShare, hhiPre: m.hhiPre, hhiPost: m.hhiPost, deltaHhi: m.deltaHhi, cr4Post: m.cr4Post, concentrationRatioN: 4,
    band: m.band, assessment: m.assessment, assessmentAr: m.assessmentAr, overlap: m.overlap ?? false, mostAffected: m.mostAffected ?? false,
    partiesSalesInMarketAed: m.partiesSalesInMarketAed, lines: linesOf(m.lines as Line[] | undefined),
  }));
}

/**
 * The market the agents read (finding 7.1): the platform's most affected market – among the markets where the parties overlap, the largest
 * change in HHI, then the highest HHI after the transaction – never simply the first market on the form. With no overlap anywhere there is no
 * most affected market; the first declared market is read and said to be so.
 */
export function subjectOf(board: Blackboard): Subject {
  const form = board.get<EcFormView>("form");
  const readings = readingsOf(board);
  const most = readings.find((r) => r.mostAffected);
  const reading = most ?? readings[0];
  const market = reading ? form?.markets.find((m) => m.id === reading.marketId) ?? form?.markets.find((m) => m.productMarket === reading.productMarket) : form?.markets[0];
  return { market, reading, mostAffected: !!most };
}

export const methodOf = (board: Blackboard) => board.get<CalculationMethod | null>("method") ?? null;

/**
 * What the band and the change in HHI mean against the published method's marks – the platform's own reading (CompetitionRules
 * .ConcentrationReading): a strong structural indication in a highly concentrated market from the presumption mark, potential concerns from
 * the minor mark there and from the significant mark in a moderately concentrated market, otherwise none on structure alone.
 */
export function structuralTone(r: Pick<MarketReading, "band" | "deltaHhi" | "overlap">, bands: MethodBands | null | undefined): Tone {
  if (!r.overlap || !bands) return "ok";
  if (r.band === "HighlyConcentrated" && r.deltaHhi >= bands.deltaHhiPresumption) return "risk";
  if (r.band === "HighlyConcentrated" && r.deltaHhi >= bands.deltaHhiMinor) return "warn";
  if (r.band === "ModeratelyConcentrated" && r.deltaHhi >= bands.deltaHhiSignificant) return "warn";
  return "ok";
}

/** A clock the file's procedure defines (the completion period, the review period, the comment window), with its days and its legal basis. */
export function clockSpec(workflow: WorkflowInstanceView | null | undefined, key: string): SlaSpec | undefined {
  return workflow?.definition.nodes.flatMap((n) => n.clocks ?? []).find((c) => c.key === key);
}
